import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {ApiDetails, KEY, SITE_KEY} from "../../dummyData";
import {ErrorMessage, Field, Form, Formik} from "formik";
import {basicSchema} from "../../components/schemas/Schemas";
import CryptoJS from "crypto-js";
import logo from "../../assets/react-logo-no-background.svg";
import {
    TextField,
    InputAdornment,
    Card,
    CardContent,
    Typography,
    Box,
    Container,
    Grid,
    IconButton,
    Paper,
    Divider,
    Link,
    FormHelperText,
    FormControl,
    Stepper,
    Step,
    StepLabel,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import {
    Login as LoginIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    PersonAdd as PersonAddIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    AccountCircle as AccountCircleIcon,
    ArrowForward as ArrowForwardIcon,
    LockOutlined,
} from "@mui/icons-material";
import {Autocomplete} from "@mui/material";

export default function SignUp(props) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [loading, setLoading] = React.useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [activeStep, setActiveStep] = useState(0);

    const navigate = useNavigate();

    const [userTypes, setUserTypes] = React.useState([]);
    const [selectedUserType, setSelectedUserType] = React.useState();

    // Fetch user types on component mount
    useEffect(() => {
        fetch(ApiDetails + "pegasus/visionary/extras/user_types", {
            method: "GET",
        })
            .then(async (response) => {
                let data = await response.json();
                if (response.status === 200) {
                    setUserTypes(data);
                } else {
                    props.snackBar({
                        text: "Failed to fetch user types.",
                        type: "error",
                    });
                }
            })
            .catch((err) => {
                console.log(err);
                props.snackBar({
                    text: "Something Went Wrong. Try Again Later",
                    type: "error",
                });
            });
    }, []);

    // Handle form submission
    const onSubmit = (values) => {
        setLoading(true);
        if (selectedUserType === undefined || selectedUserType === null) {
            props.snackBar({
                text: "Kindly select user type",
                type: "error",
            });
            setLoading(false);
            return;
        }

        let body = {
            first_name: values.signupFirstName,
            last_name: values.signupLastName,
            email_address: values.signupEmail,
            password: values.signupPassword,
            phone_number: values.phoneNumber,
        };

        body = {...body, user_type: selectedUserType.id};

        fetch(ApiDetails + "pegasus/visionary/authorization/createAccount", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
        })
            .then(async (response) => {
                let data = await response.json();
                if (response.status === 200) {
                    props.snackBar({text: "Signup Successful", type: "success"});
                    const map1 = new Map(Object.entries(data));

                    let myMap = CryptoJS.AES.encrypt(
                        JSON.stringify(Array.from(map1.entries())),
                        KEY
                    ).toString();

                    localStorage.setItem("myMap", myMap);
                    localStorage.setItem("isNew", data.new);

                    setLoading(false);

                    navigate("/home", {
                        state: {
                            userDetails: data,
                        },
                    });
                } else if (response.status === 400) {
                    props.snackBar({
                        text: "User with provided email address already exists.",
                        type: "error",
                    });
                    setLoading(false);
                } else {
                    props.snackBar({
                        text: "Signup Failed.",
                        type: "error",
                    });
                    setLoading(false);
                }
            })
            .catch((err) => {
                setLoading(false);
                props.snackBar({
                    text: "Something went wrong. Try again later.",
                    type: "error",
                });
                console.log(err);
            });
    };

    const initialValues = {
        signupFirstName: "",
        signupLastName: "",
        signupEmail: "",
        signupPassword: "",
        signupConfirmPassword: "",
        phoneNumber: "254",
    };

    // Form step navigation
    const handleNextStep = () => {
        setActiveStep((prevStep) => prevStep + 1);
    };

    const handlePrevStep = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    // Password visibility toggle
    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    // Steps for the signup stepper
    const steps = [
        'Personal Information',
        'Account Details',
        'Account Type'
    ];

    return (
        <div
            style={{zoom: "80%",}}
        >
            <Box
                sx={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#FBF4F0",
                    overflow: "auto",
                    py: 3,
                }}
            >
                <Container maxWidth="lg" sx={{position: "relative", zIndex: 1}}>
                    <Card
                        elevation={8}
                        sx={{
                            borderRadius: 4,
                            overflow: "hidden",
                            width: "100%",
                            maxWidth: "1000px",
                            mx: "auto",
                            bgcolor: "white",
                        }}
                    >
                        <Grid container>
                            {/* Form Section */}
                            <Grid
                                item
                                xs={12}
                                md={7}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    p: 4,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        mb: 3,
                                    }}
                                >
                                    <img
                                        src={logo}
                                        alt="Company Logo"
                                        style={{
                                            width: "150px",
                                            marginBottom: "16px",
                                        }}
                                    />
                                    <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
                                        Create Account
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary" textAlign="center">
                                        Join our property management platform and start managing your properties
                                        efficiently
                                    </Typography>
                                </Box>

                                {!isMobile && (
                                    <Stepper activeStep={activeStep} sx={{mb: 4}}>
                                        {steps.map((label) => (
                                            <Step key={label}>
                                                <StepLabel>{label}</StepLabel>
                                            </Step>
                                        ))}
                                    </Stepper>
                                )}

                                <Formik
                                    initialValues={initialValues}
                                    validationSchema={basicSchema}
                                    onSubmit={onSubmit}
                                >
                                    {(formikProps) => (
                                        <Form noValidate>
                                            {activeStep === 0 && (
                                                <Box>
                                                    <Typography variant="h6" gutterBottom sx={{ml: 1}}>
                                                        Personal Information
                                                    </Typography>

                                                    <Field name="signupFirstName">
                                                        {({field, meta}) => (
                                                            <FormControl fullWidth sx={{mb: 2}}>
                                                                <TextField
                                                                    {...field}
                                                                    type="text"
                                                                    label="First Name"
                                                                    variant="outlined"
                                                                    error={meta.touched && Boolean(meta.error)}
                                                                    helperText={meta.touched && meta.error}
                                                                    InputProps={{
                                                                        startAdornment: (
                                                                            <InputAdornment position="start">
                                                                                <PersonIcon color="primary"/>
                                                                            </InputAdornment>
                                                                        ),
                                                                    }}
                                                                />
                                                            </FormControl>
                                                        )}
                                                    </Field>

                                                    <Field name="signupLastName">
                                                        {({field, meta}) => (
                                                            <FormControl fullWidth sx={{mb: 2}}>
                                                                <TextField
                                                                    {...field}
                                                                    type="text"
                                                                    label="Last Name"
                                                                    variant="outlined"
                                                                    error={meta.touched && Boolean(meta.error)}
                                                                    helperText={meta.touched && meta.error}
                                                                    InputProps={{
                                                                        startAdornment: (
                                                                            <InputAdornment position="start">
                                                                                <PersonIcon color="primary"/>
                                                                            </InputAdornment>
                                                                        ),
                                                                    }}
                                                                />
                                                            </FormControl>
                                                        )}
                                                    </Field>

                                                    <Field name="phoneNumber">
                                                        {({field, meta}) => (
                                                            <FormControl fullWidth sx={{mb: 2}}>
                                                                <TextField
                                                                    {...field}
                                                                    type="tel"
                                                                    label="Phone Number"
                                                                    variant="outlined"
                                                                    error={meta.touched && Boolean(meta.error)}
                                                                    helperText={meta.touched && meta.error}
                                                                    InputProps={{
                                                                        startAdornment: (
                                                                            <InputAdornment position="start">
                                                                                <PhoneIcon color="primary"/>
                                                                            </InputAdornment>
                                                                        ),
                                                                    }}
                                                                />
                                                            </FormControl>
                                                        )}
                                                    </Field>

                                                    <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 2}}>
                                                        <LoadingButton
                                                            variant="contained"
                                                            endIcon={<ArrowForwardIcon/>}
                                                            onClick={handleNextStep}
                                                            sx={{
                                                                px: 3,
                                                                py: 1.2,
                                                                borderRadius: 2
                                                            }}
                                                        >
                                                            Next
                                                        </LoadingButton>
                                                    </Box>
                                                </Box>
                                            )}

                                            {activeStep === 1 && (
                                                <Box>
                                                    <Typography variant="h6" gutterBottom sx={{ml: 1}}>
                                                        Account Details
                                                    </Typography>

                                                    <Field name="signupEmail">
                                                        {({field, meta}) => (
                                                            <FormControl fullWidth sx={{mb: 2}}>
                                                                <TextField
                                                                    {...field}
                                                                    type="email"
                                                                    label="Email Address"
                                                                    variant="outlined"
                                                                    error={meta.touched && Boolean(meta.error)}
                                                                    helperText={meta.touched && meta.error}
                                                                    InputProps={{
                                                                        startAdornment: (
                                                                            <InputAdornment position="start">
                                                                                <EmailIcon color="primary"/>
                                                                            </InputAdornment>
                                                                        ),
                                                                    }}
                                                                />
                                                            </FormControl>
                                                        )}
                                                    </Field>

                                                    <Field name="signupPassword">
                                                        {({field, meta}) => (
                                                            <FormControl fullWidth sx={{mb: 2}}>
                                                                <TextField
                                                                    {...field}
                                                                    type={showPassword ? "text" : "password"}
                                                                    label="Password"
                                                                    variant="outlined"
                                                                    error={meta.touched && Boolean(meta.error)}
                                                                    helperText={meta.touched && meta.error}
                                                                    InputProps={{
                                                                        startAdornment: (
                                                                            <InputAdornment position="start">
                                                                                <LockOutlined color="primary"/>
                                                                            </InputAdornment>
                                                                        ),
                                                                        endAdornment: (
                                                                            <InputAdornment position="end">
                                                                                <IconButton
                                                                                    aria-label="toggle password visibility"
                                                                                    onClick={togglePasswordVisibility}
                                                                                    edge="end"
                                                                                >
                                                                                    {showPassword ?
                                                                                        <VisibilityOffIcon/> :
                                                                                        <VisibilityIcon/>}
                                                                                </IconButton>
                                                                            </InputAdornment>
                                                                        ),
                                                                    }}
                                                                />
                                                            </FormControl>
                                                        )}
                                                    </Field>

                                                    <Field name="signupConfirmPassword">
                                                        {({field, meta}) => (
                                                            <FormControl fullWidth sx={{mb: 2}}>
                                                                <TextField
                                                                    {...field}
                                                                    type={showPassword ? "text" : "password"}
                                                                    label="Confirm Password"
                                                                    variant="outlined"
                                                                    error={meta.touched && Boolean(meta.error)}
                                                                    helperText={meta.touched && meta.error}
                                                                    InputProps={{
                                                                        startAdornment: (
                                                                            <InputAdornment position="start">
                                                                                <LockOutlined color="primary"/>
                                                                            </InputAdornment>
                                                                        ),
                                                                        endAdornment: (
                                                                            <InputAdornment position="end">
                                                                                <IconButton
                                                                                    aria-label="toggle password visibility"
                                                                                    onClick={togglePasswordVisibility}
                                                                                    edge="end"
                                                                                >
                                                                                    {showPassword ?
                                                                                        <VisibilityOffIcon/> :
                                                                                        <VisibilityIcon/>}
                                                                                </IconButton>
                                                                            </InputAdornment>
                                                                        ),
                                                                    }}
                                                                />
                                                            </FormControl>
                                                        )}
                                                    </Field>

                                                    <Box sx={{display: 'flex', justifyContent: 'space-between', mt: 2}}>
                                                        <LoadingButton
                                                            variant="outlined"
                                                            onClick={handlePrevStep}
                                                            sx={{px: 3, py: 1.2, borderRadius: 2}}
                                                        >
                                                            Back
                                                        </LoadingButton>
                                                        <LoadingButton
                                                            variant="contained"
                                                            endIcon={<ArrowForwardIcon/>}
                                                            onClick={handleNextStep}
                                                            sx={{px: 3, py: 1.2, borderRadius: 2}}
                                                        >
                                                            Next
                                                        </LoadingButton>
                                                    </Box>
                                                </Box>
                                            )}

                                            {activeStep === 2 && (
                                                <Box>
                                                    <Typography variant="h6" gutterBottom sx={{ml: 1}}>
                                                        Account Type
                                                    </Typography>

                                                    <Box sx={{mb: 3}}>
                                                        <Autocomplete
                                                            disablePortal
                                                            id="user-type-select"
                                                            options={userTypes.map((type) => type.description)}
                                                            onChange={(e, value) => {
                                                                setSelectedUserType(
                                                                    userTypes.filter((type) => {
                                                                        if (type.description === value) {
                                                                            return type.id;
                                                                        }
                                                                    })[0]
                                                                );
                                                            }}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label="Account Type"
                                                                    variant="outlined"
                                                                    InputProps={{
                                                                        ...params.InputProps,
                                                                        startAdornment: (
                                                                            <>
                                                                                <InputAdornment position="start">
                                                                                    <AccountCircleIcon color="primary"/>
                                                                                </InputAdornment>
                                                                                {params.InputProps.startAdornment}
                                                                            </>
                                                                        ),
                                                                    }}
                                                                />
                                                            )}
                                                        />
                                                        <FormHelperText>
                                                            Select the type of account that best describes your role
                                                        </FormHelperText>
                                                    </Box>

                                                    <Box sx={{display: 'flex', justifyContent: 'space-between', mt: 4}}>
                                                        <LoadingButton
                                                            variant="outlined"
                                                            onClick={handlePrevStep}
                                                            sx={{px: 3, py: 1.2, borderRadius: 2}}
                                                        >
                                                            Back
                                                        </LoadingButton>
                                                        <LoadingButton
                                                            type="submit"
                                                            variant="contained"
                                                            endIcon={<PersonAddIcon/>}
                                                            loading={loading}
                                                            loadingPosition="end"
                                                            sx={{
                                                                px: 3,
                                                                py: 1.2,
                                                                borderRadius: 2,
                                                                fontWeight: "bold"
                                                            }}
                                                        >
                                                            Create Account
                                                        </LoadingButton>
                                                    </Box>
                                                </Box>
                                            )}

                                            <Divider sx={{my: 4}}/>

                                            <Box sx={{textAlign: "center"}}>
                                                <Typography variant="body2">
                                                    Already have an account?{" "}
                                                    <Link href="/" fontWeight="bold" underline="hover">
                                                        Sign In
                                                    </Link>
                                                </Typography>
                                            </Box>
                                        </Form>
                                    )}
                                </Formik>
                            </Grid>

                            {/* Image Section */}
                            <Grid
                                item
                                xs={12}
                                md={5}
                                sx={{
                                    display: {xs: "none", md: "block"},
                                    position: "relative",
                                    bgcolor: "#f0f7ff",
                                }}
                            >
                                <Box
                                    component="img"
                                    src={require("../../assets/img1.jpg")}
                                    alt="Property Management"
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        objectPosition: "center",
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: "absolute",
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        p: 4,
                                        background: "linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0))",
                                        color: "white",
                                    }}
                                >
                                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                                        Join Our Property Management Platform
                                    </Typography>
                                    <Typography variant="body1">
                                        Create an account today to access our suite of powerful property management
                                        tools.
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Card>
                </Container>
            </Box>
        </div>
    );
}